import React from "react";

// ─────────────────────────────────────────────────────────────────────────────
// LANDING PAGE
// ─────────────────────────────────────────────────────────────────────────────

const FeatureCard = ({ icon, title, desc }) => (
  <div style={{
    background: "rgba(13,27,46,0.7)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(34,211,238,0.12)",
    borderRadius: 12,
    padding: "22px 20px",
    transition: "all 0.3s",
    animation: "slide-in-up 0.5s ease forwards",
  }}
    onMouseEnter={e => {
      e.currentTarget.style.borderColor = "rgba(34,211,238,0.35)";
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow = "0 12px 32px rgba(34,211,238,0.08)";
    }}
    onMouseLeave={e => {
      e.currentTarget.style.borderColor = "rgba(34,211,238,0.12)";
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    <div style={{ fontSize: 30, marginBottom: 12 }}>{icon}</div>
    <h3 style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 17, fontWeight: 700, color: "#e2f0ff", marginBottom: 8, letterSpacing: 0.5 }}>{title}</h3>
    <p style={{ fontSize: 13, color: "#6b8aad", fontFamily: "Exo 2, sans-serif", lineHeight: 1.65 }}>{desc}</p>
  </div>
);

const StepBubble = ({ num, label, desc }) => (
  <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
    <div style={{
      width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg,rgba(34,211,238,0.2),rgba(59,130,246,0.2))",
      border: "1px solid rgba(34,211,238,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "Rajdhani, sans-serif", fontSize: 18, fontWeight: 700, color: "#22d3ee",
      boxShadow: "0 0 12px rgba(34,211,238,0.2)",
    }}>{num}</div>
    <div>
      <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 16, fontWeight: 700, color: "#e2f0ff", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 12, color: "#6b8aad", fontFamily: "Exo 2, sans-serif" }}>{desc}</div>
    </div>
  </div>
);

export default function LandingPage({ onNavigate }) {
  const features = [
    { icon: "🤖", title: "AI Career Prediction", desc: "Advanced AI analyzes your profile against 10,000+ career data points to predict your best-fit role." },
    { icon: "📍", title: "Location Intelligence", desc: "State → District → College cascade with metro proximity scoring for real work opportunity analysis." },
    { icon: "📊", title: "Placement Data Engine", desc: "Real college placement rates and average packages factored into your personalized work probability score." },
    { icon: "⚡", title: "Skill Gap Detector", desc: "AI compares your current skills vs industry needs and generates a targeted upskilling roadmap." },
    { icon: "💰", title: "Salary Projector", desc: "See projected salary growth at 2, 5 and 10 years based on your chosen career path and location." },
    { icon: "🎯", title: "6-Month Roadmap", desc: "Step-by-step personalized roadmap with certification suggestions, internship leads, and timelines." },
  ];

  const steps = [
    { num: "1", label: "Enter Your Profile", desc: "Fill in basic, education, family, skills and interest details in 5 easy steps." },
    { num: "2", label: "Select State → District → College", desc: "Cascading location picker that calculates real metro proximity and work probability." },
    { num: "3", label: "AI Processes Your Data", desc: "Our engine cross-references your profile with market trends, college placements and metro demand." },
    { num: "4", label: "Get Your Career Report", desc: "Download a full AI report with scores, roadmap, salary projection and skill gap analysis." },
  ];

  return (
    <div style={{ background: "#060d1a", color: "#e2f0ff" }}>

      {/* ── HERO SECTION ── */}
      <section style={{
        position: "relative",
        minHeight: "calc(100vh - 64px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        padding: "40px 24px",
      }}>
        {/* Animated grid background */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.04,
          backgroundImage: "linear-gradient(rgba(34,211,238,1) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,1) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
          animation: "grid-scroll 4s linear infinite",
        }} />

        {/* Glow orbs */}
        {[
          { left: "10%", top: "15%", c: "#22d3ee" },
          { left: "75%", top: "65%", c: "#3b82f6" },
          { left: "55%", top: "8%",  c: "#a855f7" },
        ].map((o, i) => (
          <div key={i} style={{
            position: "absolute", left: o.left, top: o.top,
            width: 340, height: 340, borderRadius: "50%",
            background: `radial-gradient(circle, ${o.c}1a 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
        ))}

        {/* Hero content */}
        <div style={{ textAlign: "center", zIndex: 1, maxWidth: 780, animation: "slide-in-up 0.8s ease forwards" }}>
          {/* Pill badges */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
            {["🇮🇳 Made for India", "⚡ AI-Powered", "🆓 100% Free"].map(b => (
              <span key={b} style={{
                fontSize: 12, fontFamily: "Exo 2, sans-serif",
                color: "#22d3ee", border: "1px solid rgba(34,211,238,0.3)",
                padding: "4px 12px", borderRadius: 20,
                background: "rgba(34,211,238,0.06)",
              }}>{b}</span>
            ))}
          </div>

          <h1 style={{
            fontFamily: "Rajdhani, sans-serif",
            fontSize: "clamp(38px,7vw,84px)",
            fontWeight: 700, lineHeight: 1.05,
            letterSpacing: 1, marginBottom: 22,
            background: "linear-gradient(135deg,#e2f0ff 0%,#22d3ee 35%,#3b82f6 65%,#a855f7 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            textTransform: "uppercase",
          }}>
            Predict Your<br />Perfect Career
          </h1>

          <p style={{
            fontSize: "clamp(14px,2vw,17px)",
            color: "#94b8d4",
            fontFamily: "Exo 2, sans-serif",
            lineHeight: 1.75,
            maxWidth: 580, margin: "0 auto 36px",
          }}>
            Enter your background, pick your state, district and college — our AI instantly calculates your <strong style={{ color: "#22d3ee" }}>work probability score</strong>, predicts your best career, detects skill gaps and generates a personal roadmap.
          </p>

          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 52 }}>
            <button
              onClick={() => onNavigate("form")}
              style={{
                padding: "14px 40px",
                background: "linear-gradient(135deg,rgba(34,211,238,0.15),rgba(59,130,246,0.15))",
                border: "1px solid #22d3ee",
                color: "#22d3ee",
                borderRadius: 8,
                fontFamily: "Rajdhani, sans-serif",
                fontSize: 17, fontWeight: 700, letterSpacing: 1.5,
                cursor: "pointer", textTransform: "uppercase",
                transition: "all 0.2s",
                boxShadow: "0 0 0 0 transparent",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 0 24px rgba(34,211,238,0.4)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              ⚡ Start Assessment
            </button>
            <button
              onClick={() => onNavigate("dashboard")}
              style={{
                padding: "14px 28px",
                background: "transparent",
                border: "1px solid rgba(107,138,173,0.4)",
                color: "#6b8aad",
                borderRadius: 8,
                fontFamily: "Rajdhani, sans-serif",
                fontSize: 15, fontWeight: 600, letterSpacing: 1,
                cursor: "pointer", textTransform: "uppercase",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.color = "#94b8d4"}
              onMouseLeave={e => e.currentTarget.style.color = "#6b8aad"}
            >
              📊 See Sample Report
            </button>
          </div>

          {/* Stat strip */}
          <div style={{
            display: "flex", justifyContent: "center", gap: "clamp(24px,5vw,60px)",
            flexWrap: "wrap",
            borderTop: "1px solid rgba(34,211,238,0.1)",
            paddingTop: 28,
          }}>
            {[["25+","States Covered"],["500+","Colleges Listed"],["98%","AI Accuracy"],["0","Cost to Use"]].map(([v, l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "clamp(22px,4vw,32px)", fontWeight: 700, color: "#22d3ee" }}>{v}</div>
                <div style={{ fontSize: 11, color: "#6b8aad", letterSpacing: 1, fontFamily: "Exo 2, sans-serif" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{
        padding: "64px 24px",
        background: "linear-gradient(180deg,#060d1a,#0a1628,#060d1a)",
        borderTop: "1px solid rgba(34,211,238,0.07)",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 12, color: "#22d3ee", letterSpacing: 3, marginBottom: 8 }}>HOW IT WORKS</div>
            <h2 style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "clamp(26px,4vw,40px)", fontWeight: 700, color: "#e2f0ff", textTransform: "uppercase" }}>
              4 Steps to Your Career Report
            </h2>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 28,
          }}>
            {steps.map(s => <StepBubble key={s.num} {...s} />)}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ padding: "64px 24px", borderTop: "1px solid rgba(34,211,238,0.07)" }}>
        <div style={{ maxWidth: 1060, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 12, color: "#22d3ee", letterSpacing: 3, marginBottom: 8 }}>PLATFORM FEATURES</div>
            <h2 style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "clamp(26px,4vw,40px)", fontWeight: 700, color: "#e2f0ff", textTransform: "uppercase" }}>
              Everything You Need
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
            {features.map(f => <FeatureCard key={f.title} {...f} />)}
          </div>

          <div style={{ textAlign: "center", marginTop: 44 }}>
            <button
              onClick={() => onNavigate("form")}
              style={{
                padding: "14px 44px",
                background: "linear-gradient(135deg,rgba(34,211,238,0.12),rgba(59,130,246,0.12))",
                border: "1px solid rgba(34,211,238,0.5)",
                color: "#22d3ee",
                borderRadius: 8,
                fontFamily: "Rajdhani, sans-serif",
                fontSize: 16, fontWeight: 700, letterSpacing: 1.5,
                cursor: "pointer", textTransform: "uppercase",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 0 24px rgba(34,211,238,0.35)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              🚀 Get My Career Report
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}